import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { failed, success } from "../../common/Toastify";
import Table from "react-bootstrap/Table";
import { Skeleton, TableCell, Tooltip } from "@mui/material";
import CustomPagination from "../../common/components/Pagination/CustomPagination";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Select from "react-select";
import {
  getCTIData,
  getCTIDataByContactID,
  getCTIDataByEncounterId,
  getCTIDataByPractitionerId,
  getPreSignedURL,
  retryCallRecordingFetch,
  uploadMediaToS3,
} from "../../api/CTI";
import { FaInfoCircle, FaPlay,FaMicrophoneSlash } from "react-icons/fa";
import { capitalizeWords } from "../../common/functions";
import { Col, Modal, Row } from "react-bootstrap";
import SelectFiled from "../../common/textfield/SelectFiled";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useOrgQuery } from "../../../hooks/ReactQueryHooks/useOrgQuery";
import { FullPageSpinner } from "../../common/Spinner/FullPageSpinner";
import { Button } from "../../common/Button";
import TextInput from "../../common/textfield/TextInput";
import DateSelector from "../../common/textfield/DateSelector";
import moment from "moment";
import { getPractitionerAuthorizedList } from "../../api/Individual";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import { validation, conditionalValidation } from "../../../utils/constants";

function CTIDashboard() {
  const [CTIData, setCTIData] = useState([]);

  const [organizations, setOrganizations] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const orgId = useSelector((state) => state?.auth?.user?.organizations);
  const userRoles = useSelector(
    (state) => state?.auth?.user?.["cognito:groups"]
  );
  const admin = userRoles?.includes("Super-Admin");
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [practitionerOptions, setPractitionerOptions] = useState([]);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [sortByEndTime, setSortByEndTime] = useState("ASC");
  const [sortByStartTime, setSortByStartTime] = useState("ASC");
  const [sortByContactId, setSortByContactId] = useState("ASC");
  const formik = useFormik({
    initialValues: {
      orgId: "", //orgId?.[0]?.id,
      contactId: "",
      practitionerId: "",
      practitionerName: "",
      startDate: moment().subtract(2, "days"),
      endDate: moment(),
      encounterId: "",
      ani: "",
      dni: "",
    },
    validationSchema: Yup.object({
        contactId: conditionalValidation(
          Yup.string(),
          {
            validation
          }),
        encounterId: conditionalValidation(
          Yup.string(),
          {
            validation
          }),
        ani: conditionalValidation(
          Yup.string(),
          {
            validation
          }),
        dni: conditionalValidation(
          Yup.string(),
          {
            validation
          })
    }),
  });

  const handleOrgChange = (event) => {
    let value = event?.target?.value;
    formik.setFieldValue("orgId", value);
    setPage(0);
  };

  useOrgQuery({
    onSuccess: (data) => {
      setOrganizations(
        data?.map((org) => ({ name: org?.name, value: org?.id }))
      );
    },
    refetchOnWindowFocus: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [playerUrl, setPlayerUrl] = useState(null);

  const handlePlay = (url) => setPlayerUrl(url);
  const handleClose = () => setPlayerUrl(null);

  const handlePlayFromS3 = async (data) => {
    setSelectedRecord(data);
    setIsPageLoading(true);
    getPreSignedURL(data?.object_url, formik.values?.orgId)
      .then((res) => {
        if (res?.url) {
          handlePlay(res?.url);
        }
      })
      .catch((error) => failed(error?.error || "Error getting recording"))
      .finally(() => setIsPageLoading(false));
  };

  const handleRetry = async (data) => {
    setSelectedRecord(data);
    setIsPageLoading(true);
    retryCallRecordingFetch(data?.contactId, formik.values.orgId)
      .then((res) => {
        if (res?.media) {
          handlePlay(res?.media);
          let payload = {
            mediaURL: res?.media,
            metadata: data,
          };
          uploadMediaToS3(payload, formik.values?.orgId).then((res) => {
            if (res?.path) {
              success("Call recording extracted successfully");
              setFilteredData((prevData) =>
                prevData?.map((item) =>
                  item?.contactId === data?.contactId
                    ? { ...item, object_url: res.path }
                    : item
                )
              );
            }
          });
        } else {
          failed("Call recording not found!");
        }
      })
      .catch((res) => failed(res?.message || res?.error))
      .finally(() => setIsPageLoading(false));
  };

  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const totalRows = filteredData?.length;
  const fetchCTIData = (filter = false) => {
    setIsLoading(true);
    getCTIData(formik.values.orgId)
      .then((res) => {
        let data = res?.data?.Items || [];

        // Filter by date if needed
        if (filter) {
          data = filterDataBasedOnDate(data);
        }

        // Filter by ANI and/or DNI
        const { ani, dni } = formik.values;
        if (ani && dni) {
          data = data.filter((item) => item?.ani === ani && item?.dni === dni);
        } else if (ani) {
          data = data.filter((item) => item?.ani === ani);
        } else if (dni) {
          data = data.filter((item) => item?.dni === dni);
        }

        setCTIData(res?.data?.Items);
        setFilteredData(data);
      })
      .catch((res) => {
        failed(
          res?.response?.data?.message ||
            res?.response?.data?.error ||
            res.message
        );
      })
      .finally(() => setIsLoading(false));
  };

  const computeOptions = (org) => {
    let res = org.filter((org) => org.value !== orgId?.[0]?.id);
    let firstOrg;
    if (admin) {
      firstOrg = { name: "All", value: "All" };
      return [firstOrg, ...org];
    }
    return [{ name: orgId?.[0]?.name, value: orgId?.[0]?.id }, ...res];
  };

  useEffect(() => {
    if (formik.values.orgId) {
      getPractitionerAuthorizedList(formik.values?.orgId, null, null, true).then((res) => {
        if (res?.status === true) {
          let practitionerOptionsToDisplay = res?.result
            .map((practitioner) => ({
              value: practitioner?.id,
              label: capitalizeWords(practitioner?.name?.[0]?.text),
              data: practitioner,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
          setPractitionerOptions(practitionerOptionsToDisplay);
        }
      });
    }
  }, [formik.values?.orgId]);

  const handleSearchClick = async () => {
    if (formik.values?.contactId) {
      setIsPageLoading(true);
      formik.setFieldValue("practitionerId", "");
      formik.setFieldValue("practitionerName", "");
      formik.setFieldValue("encounterId", "");
      formik.setFieldValue("ani", "");
      formik.setFieldValue("dni", "");
      setIsPageLoading(true);
      getCTIDataByContactID(formik.values.contactId, formik.values.orgId)
        .then((res) => {
          setCTIData(res?.data?.Items);
          setFilteredData(res?.data?.Items);
        })
        .catch((error) => failed(error?.error || "Error getting CTI Data"))
        .finally(() => setIsPageLoading(false));
    } else if (formik.values?.encounterId) {
      setIsPageLoading(true);
      formik.setFieldValue("contactId", "");
      formik.setFieldValue("practitionerId", "");
      formik.setFieldValue("practitionerName", "");
      formik.setFieldValue("ani", "");
      formik.setFieldValue("dni", "");

      setIsPageLoading(true);
      getCTIDataByEncounterId(formik.values?.encounterId, formik.values.orgId)
        .then((res) => {
          setCTIData(res?.data?.Items);
          setFilteredData(res?.data?.Items);
        })
        .catch((error) => failed(error?.error || "Error getting CTI Data"))
        .finally(() => setIsPageLoading(false));
    } else if (formik?.values?.practitionerId) {
      setIsPageLoading(true);
      getCTIDataByPractitionerId(
        formik.values?.practitionerId,
        formik.values?.orgId
      )
        .then((res) => {
          setCTIData(res?.data?.Items);
          if (formik.values.startDate) {
            const FMT_FORM = "MM/DD/YYYY";
            const FMT_END = "M/DD/YYYY h:mm:ss A";
            const lower = formik.values?.startDate
              ? moment(formik.values?.startDate, FMT_FORM).startOf("day")
              : null;
            const upper = formik.values?.endDate
              ? moment(formik.values?.endDate, FMT_FORM).endOf("day")
              : null;

            const filtered = res?.data?.Items?.filter((row) => {
              const callEnd = moment(row.startTime, FMT_END);

              if (lower && (!callEnd ||  callEnd.isBefore(lower))) {
                return false;
              }
              if (upper && (!callEnd || callEnd.isAfter(upper))) {
                return false;
              }

              return true;
            });
            setFilteredData(filtered);
          } else {
            setFilteredData(res?.data?.Items);
          }
        })
        .finally(() => setIsPageLoading(false));
    } else if (formik.values.startDate) {
      fetchCTIData(true);
    } else {
      fetchCTIData();
    }
  };

  const handlePractitionerChange = (event) => {
    setSelectedPractitioner(event);
    formik.setFieldValue("contactId", "");
    formik.setFieldValue("encounterId", "");

    formik.setFieldValue("practitionerId", event?.value || "");
    formik.setFieldValue("practitionerName", event?.label || "");
  };

  const filterDataBasedOnDate = (data) => {
    const FMT_FORM = "MM/DD/YYYY";
    const FMT_END = "M/DD/YYYY h:mm:ss A";
    const lower = formik.values?.startDate
      ? moment(formik.values?.startDate, FMT_FORM).startOf("day")
      : null;
    const upper = formik.values?.endDate
      ? moment(formik.values?.endDate, FMT_FORM).endOf("day")
      : null;

    const filtered = data.filter((row) => {
      //const callEnd = (row.endTime) ? moment(row.endTime, FMT_END) : null;
      const callEnd = moment(row.startTime, FMT_END);
      if (lower && (!callEnd || callEnd.isBefore(lower))) {
        return false;
      }
      if (upper && (!callEnd || callEnd.isAfter(upper))) {
        return false;
      }
      return true;
    });
    return filtered;
  };

  const handlePreviewEncounterClick = (encounterId) => {
    const baseUrl = window.location.origin;
    const url = `${baseUrl}/app/encounter-details?encounterId=${encounterId}`;
    window.open(url, "_blank");
  };

  const handleSort = (sortByColumn, sortByMethod) => {
    if (sortByColumn === "endTime") {
      setSortByEndTime(sortByMethod);
    }

    if (sortByColumn === "contactId") {
      setSortByContactId(sortByMethod);
    }

    if (sortByColumn === "startTime") {
      setSortByStartTime(sortByMethod);
    }

    const isAsc = sortByMethod === "ASC";

    const toMillis = (t) => {
      const ms = new Date(t).getTime();
      return isNaN(ms) ? (isAsc ? Number.MAX_VALUE : Number.MIN_VALUE) : ms;
    };

    let sorted = [...filteredData].sort((a, b) => {
      switch (sortByColumn) {
        case "startTime":
          return isAsc
            ? toMillis(a?.startTime) - toMillis(b?.startTime)
            : toMillis(b?.startTime) - toMillis(a?.startTime);

        case "endTime":
          return isAsc
            ? toMillis(a?.endTime) - toMillis(b?.endTime)
            : toMillis(b?.endTime) - toMillis(a?.endTime);

        case "contactId": {
          const res = compareBigNumericStrings(
            a?.contactId ?? "",
            b?.contactId ?? ""
          );
          return isAsc ? res : -res;
        }

        default:
          return 0;
      }
    });

    setFilteredData(sorted);
  };

  const compareBigNumericStrings = (aStr, bStr) => {
    if (aStr.length !== bStr.length) return aStr.length - bStr.length;

    return aStr.localeCompare(bStr);
  };

  const handleClearClick = async () => {
    formik.setFieldValue("practitionerId", "");
    formik.setFieldValue("practitionerName", "");
    formik.setFieldValue("encounterId", "");
    formik.setFieldValue("ani", "");
    formik.setFieldValue("dni", "");
    formik.setFieldValue("contactId", "");
    formik.setFieldValue("startDate", moment().subtract(2, "days"));
    formik.setFieldValue("endDate", moment());
    formik.setFieldValue("orgId", "");
    setSelectedPractitioner(null);
    setCTIData([]);
    setFilteredData([]);
  };

  return (
    <section className="common-listing">
      {isPageLoading && <FullPageSpinner loadingText="Loading..." />}

      <div className="px-3">
        <form className="common-form border-fields dropdown-filter-rw">
          <Row>
            <Col md={2}>
              <SelectFiled
                keyField={"orgId"}
                label={"Select Organization"}
                formik={formik}
                selectLabel={"Select Organization"}
                options={computeOptions(organizations)}
                onChange={handleOrgChange}
                required={false}
                optional={false}
                readOnly={isLoading}
              />
            </Col>
            {!formik.values?.encounterId && (
              <Col md={2}>
                <TextInput
                  keyField={"contactId"}
                  label={"Contact ID"}
                  formik={formik}
                  placeholder={"Contact Id"}
                  hideRequired={true}
                />
              </Col>
            )}
            {!formik.values?.contactId && (
              <Col md={2}>
                <TextInput
                  keyField={"encounterId"}
                  label={"Encounter ID"}
                  formik={formik}
                  placeholder={"Encounter ID"}
                  hideRequired={true}
                />
              </Col>
            )}
            {!formik.values?.contactId && !formik.values?.encounterId && (
              <Col md={2}>
                <div className="field-wrap flex-fill">
                  <label htmlFor={"select"}>{"Practitioner"}</label>
                  <Select
                    onChange={handlePractitionerChange}
                    isMulti={false}
                    name="practitioner-filter"
                    value={selectedPractitioner}
                    options={practitionerOptions}
                    className="search-select"
                    classNamePrefix="select"
                    isClearable={true}
                    isLoading={practitionerOptions?.length > 0 ? false : true}
                  />
                </div>
              </Col>
            )}
            <Col md={2}>
              <Button
                style={{ marginTop: "20px", marginLeft: "40px" }}
                isLoading={isPageLoading}
                onClick={handleSearchClick}
              >
                Search
              </Button>
            </Col>

            <Col md={2}>
              <Button
                style={{ marginTop: "20px" }}
                isLoading={isPageLoading}
                variant="secondary"
                onClick={handleClearClick}
              >
                Clear
              </Button>
            </Col>
          </Row>

          <Row className="align-items-end">
            {!formik.values?.contactId && !formik.values?.encounterId && (
              <>
                <Col md={2}>
                  <DateSelector
                    keyField={"startDate"}
                    label={"Start Date"}
                    formik={formik}
                    hasValue={moment()}
                    placeholder={"Start Date"}
                    minDate={moment().subtract(100, "years")}
                    maxDate={moment()}
                    tooltip={"Filter results based on start date of the call"}
                  />
                </Col>
                <Col md={2}>
                  <DateSelector
                    keyField={"endDate"}
                    label={"End Date"}
                    formik={formik}
                    hasValue={moment()}
                    placeholder={"End Date"}
                    minDate={moment().subtract(100, "years")}
                    maxDate={moment()}
                    tooltip={"Filter results based on start date of the call"}
                  />
                </Col>
                <Col md={2}>
                  <TextInput
                    keyField={"ani"}
                    label={"ANI"}
                    formik={formik}
                    placeholder={"ANI"}
                    hideRequired={true}
                    tooltip={"The number from which the caller dialed"}
                  />
                </Col>
                <Col md={2}>
                  <TextInput
                    keyField={"dni"}
                    label={"DNI"}
                    formik={formik}
                    placeholder={"DNI"}
                    hideRequired={true}
                    tooltip={"The number dialed by the caller"}
                  />
                </Col>
              </>
            )}
          </Row>
        </form>
      </div>
      <div className="table-wrap">
        <Table responsive>
          <thead>
            <tr>
              <th
                style={{ cursor: "pointer" }}
                onClick={() =>
                  handleSort(
                    "contactId",
                    sortByContactId === "ASC" ? "DESC" : "ASC"
                  )
                }
              >
                Contact ID
                {sortByContactId === "ASC" ? (
                  <span>
                    <ArrowDropUp />
                  </span>
                ) : (
                  <span>
                    <ArrowDropDown />
                  </span>
                )}
              </th>
              <th>Encounter ID</th>
              <th>Practitioner</th>
              <th
                style={{ cursor: "pointer" }}
                onClick={() =>
                  handleSort(
                    "startTime",
                    sortByStartTime === "ASC" ? "DESC" : "ASC"
                  )
                }
              >
                Start Time
                {sortByStartTime === "ASC" ? (
                  <span>
                    <ArrowDropUp />
                  </span>
                ) : (
                  <span>
                    <ArrowDropDown />
                  </span>
                )}
              </th>
              <th
                style={{ cursor: "pointer" }}
                onClick={() =>
                  handleSort(
                    "endTime",
                    sortByEndTime === "ASC" ? "DESC" : "ASC"
                  )
                }
              >
                End Time
                {sortByEndTime === "ASC" ? (
                  <span>
                    <ArrowDropUp />
                  </span>
                ) : (
                  <span>
                    <ArrowDropDown />
                  </span>
                )}
              </th>
              <th style={{ textAlign: "center" }}>Skill</th>
              <th style={{ textAlign: "center" }}>Call Type</th>
              <th style={{ textAlign: "center" }}>
                ANI{" "}
                <Tooltip
                  title={"The number from which the caller dialed"}
                  arrow
                >
                  <InfoOutlinedIcon
                    fontSize="small"
                    style={{
                      cursor: "pointer",
                      color: "#005da8",
                      marginBottom: "4px",
                      marginLeft: "4px",
                    }}
                  />
                </Tooltip>
              </th>
              <th style={{ textAlign: "center" }}>
                DNI{" "}
                <Tooltip title={"The number dialed by the caller"} arrow>
                  <InfoOutlinedIcon
                    fontSize="small"
                    style={{
                      cursor: "pointer",
                      color: "#005da8",
                      marginBottom: "4px",
                      marginLeft: "4px",
                    }}
                  />
                </Tooltip>
              </th>
              <th style={{ textAlign: "center" }}> Call Recording</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 4 }, (_, index) => (
                <tr key={index}>
                  {Array.from({ length: 10 }).map((_, cellIndex) => (
                    <td key={cellIndex}>
                      <Skeleton animation="wave" />
                    </td>
                  ))}
                </tr>
              ))
            ) : filteredData?.length > 0 ? (
              filteredData
                ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                ?.map((data) => {
                return (
                  <tr key={data?.contactId}>
                    <td>{data?.contactId}</td>
                    <td>
                      {(data?.encounterId && (
                        <div
                          style={{
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                          onClick={() =>
                            handlePreviewEncounterClick(data?.encounterId)
                          }
                        >
                          {data?.encounterId}
                        </div>
                      )) ||
                        "-"}
                    </td>

                    <td>{capitalizeWords(data?.practitionerName) || "-"}</td>
                    <td>
                      {data?.startTime
                        ? moment
                            .utc(data?.startTime)
                            .local()
                            .format("YYYY-MM-DD HH:mm")
                        : "-"}
                    </td>
                    <td>
                        {data?.endTime
                          ? moment
                              .utc(data?.endTime)
                              .local()
                              .format("YYYY-MM-DD HH:mm")
                          : "-"}
                      </td>

                    <td style={{ textAlign: "center" }}>{data?.skillName}</td>
                    <td style={{ textAlign: "center" }}>
                      {data?.callType || "-"}
                    </td>
                    <td style={{ textAlign: "center" }}>{data?.ani}</td>
                    <td style={{ textAlign: "center" }}>{data?.dni}</td>
                    <td style={{ textAlign: "center" }}>
                      {data?.callType === "SURVEY" ? (
                        <FaMicrophoneSlash
                          style={{ cursor: "default", color: "#888" }}
                          title="No recording is saved for Survey"
                        />
                      ) : data?.object_url ? (
                        <FaPlay
                          style={{ cursor: "pointer" }}
                          title="Play recording"
                          onClick={() => handlePlayFromS3(data)}
                        />
                      ) : (
                        <FaInfoCircle
                          style={{ cursor: "pointer" }}
                          title="The CTI service failed to extract recording automatically. Click here to retry."
                          onClick={() => handleRetry(data)}
                        />
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <TableCell colSpan="5" style={{ textAlign: "center" }}>
                  No Data Available
                </TableCell>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      {filteredData?.length > 0 ? (
        <CustomPagination
          tableData={filteredData}
          totalRows={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          handlePage={(pageNo) => setPage(pageNo)}
        />
      ) : null}
      <Modal
        show={Boolean(playerUrl)}
        onHide={handleClose}
        centered
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Call Recording - {selectedRecord?.contactId}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="text-center">
          <audio src={playerUrl} controls autoPlay style={{ width: "100%" }}>
            Your browser does not support the audio element.
          </audio>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
}

export default CTIDashboard;
